// @ts-nocheck
// Customer profile card — cti-v3-style header block that appears at
// the top of the workspace whenever a caller is on the line (incoming
// or outgoing) OR whenever a callerContact was fetched for the last
// call. Reads exclusively from useCallStore.callerContact + related
// live fields — no new endpoint. Populated by the existing lookup
// (getContactGroupMemberByNumber → API_CONTACT_GROUP_MEMBER_BY_NUMBER)
// triggered from RingingScreen / OnCallScreen / CTIAgentUI persist.
//
// Layout (top → bottom):
//   Header row:
//     • Circular avatar with initials (left)
//     • Name
//     • Primary + alt numbers strip (company is a grid cell)
//     • Timer + call-state chip on the right
//   Detail grid: Street · City · State · Country · Email · Language — all
//     fields the Contacts "Create Contact" form actually saves
//     (contact_group_member columns, plus Email/Language via its
//     additional_details JSONB blob), each cell falling back to em-dash
//     when the record doesn't have it. (An earlier version of this grid
//     also showed Customer ID / Plan / Tenure / High value / Churn risk /
//     Recording consent — none of which this record has, so every one of
//     those cells always read "—". Removed rather than left as permanent
//     dead cells.)
import React, { useEffect, useMemo, useState } from 'react';
import { FiPhoneIncoming, FiPhoneOutgoing, FiMapPin, FiHome, FiFlag, FiGlobe, FiMail, FiTarget, FiTag, FiChevronDown, FiBriefcase } from 'react-icons/fi';
import { contactExtraDetails } from '../utils/contactExtraDetails';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { useCallStore } from '@/router/zustand/callStore';
import { getUser } from '@/utilities/setting';
import { getAgentMasking, maskNumber } from '@/utilities/cliDisplay';
import {
  callPhaseEvt,
  formatSeconds,
  resolveCallPhase,
  resolveCustomerName,
  resolveCustomerNumber,
} from '../ctiHelpers';
import CallQualityIndicator from './CallQualityIndicator';

const GRID_CELLS = 6;

function initials(first, last, fallbackNumber) {
  const f = String(first || '').trim();
  const l = String(last || '').trim();
  if (f || l) return ((f[0] || '') + (l[0] || '')).toUpperCase();
  const n = String(fallbackNumber || '').replace(/\D/g, '');
  return n ? n.slice(-2) : '??';
}

function fmtNumber(n) {
  if (!n) return null;
  const s = String(n).trim();
  return s || null;
}

// additional_details is a free-form JSONB blob — written either by this
// app's own Add/Edit Contact form (keys 'email'/'language', lowercase) or
// by DispositionContactSync on a disposition submit, which stores the
// admin's own disposition-field name verbatim (e.g. "Email Address"). Same
// normalize+edit-distance-≤1 matching useDispositionFlow.ts already uses
// for the same reason (tolerate a field named "Emial"/"Languge" etc.)
// rather than requiring an exact key.
const normalizeFieldName = (name) => String(name || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
function isOneEditOrLess(a, b) {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  let i = 0; let j = 0; let foundDiff = false;
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] !== longer[j]) {
      if (foundDiff) return false;
      foundDiff = true;
      if (shorter.length === longer.length) { i++; j++; } else { j++; }
    } else { i++; j++; }
  }
  return true;
}
function findAdditionalDetail(details, ...candidates) {
  if (!details || typeof details !== 'object') return null;
  for (const [k, v] of Object.entries(details)) {
    const nk = normalizeFieldName(k);
    if (!candidates.some((c) => isOneEditOrLess(nk, c))) continue;
    if (v === null || v === undefined || typeof v === 'object') continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return null;
}

export default function CustomerProfileCard({ showQualityIndicator = false } = {}) {
  const callerContact  = useCallStore((s) => s.callerContact);
  const activeCallMeta = useCallStore((s) => s.activeCallMeta);
  const callState      = useCallStore((s) => s.callState);
  const direction      = useCallStore((s) => s.direction);
  const outgoingCallStatus = useCallStore((s) => s.outgoingCallStatus);
  const customerRingingActive = useCallStore((s) => s.customerRingingActive);

  // Show the card whenever we have EITHER a fetched contact OR an
  // active call. Ensures the card appears the second a call rings
  // even before the contact-lookup resolves (name will fill in on
  // the second render when useCallStore.callerContact hydrates).
  const isActive = callState && callState !== 'idle';
  const hasContact = Boolean(callerContact);

  // resolveCustomerName/resolveCustomerNumber (ctiHelpers.tsx) are the SAME
  // resolution CTIRightRail.tsx's CallDetailsPane now uses — one place
  // decides "who is the customer" instead of two components guessing
  // independently and disagreeing.
  const fullName = resolveCustomerName(callerContact, activeCallMeta) || 'Unknown caller';
  const maskNumbers = getAgentMasking(getUser());
  const showNumber = (n) => {
    const v = fmtNumber(n);
    if (!v) return null;
    return maskNumbers ? maskNumber(v) : v;
  };
  const primary = showNumber(resolveCustomerNumber(callerContact, activeCallMeta));
  const alt1 = showNumber(callerContact?.alt1_number);
  const alt2 = showNumber(callerContact?.alt2_number);

  // Live timer — counts RING time while ringing/dialing (anchored on
  // start_time) and TALK time once the call is answered (anchored on
  // bridge_time). Switching anchors at answer is the whole point: the two
  // stretches are shown one after the other, never summed, so "on call"
  // never silently includes however long it rang. bridge_time is only ever
  // set on the real customer-leg answer (useMobileCallSocket.ts withholds
  // it for a B_PARTY/agent-leg-only ANSWERED), on both the socket-driven
  // (Mobile + WebRTC) and SIP paths.
  // Tick state forces a re-render every second so the value visibly counts
  // up; useMemo alone would only refresh on prop/state changes.
  const hasBridge = Boolean(activeCallMeta?.bridge_time);
  // Outbound: RING time starts at the other leg's first ring event
  // (ring_time), not when the call was placed — before that it stays 0.
  const isOutgoing = (activeCallMeta?.direction || direction) === 'outgoing';
  const timerAnchor =
    activeCallMeta?.bridge_time ||
    (isOutgoing ? activeCallMeta?.ring_time : activeCallMeta?.start_time) ||
    null;
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isActive || !timerAnchor) return undefined;
    const id = setInterval(() => setTick((n) => (n + 1) % 1_000_000), 1000);
    return () => clearInterval(id);
  }, [isActive, timerAnchor]);

  const durationSec = useMemo(() => {
    if (!timerAnchor) return 0;
    const t0 = new Date(timerAnchor).getTime();
    if (!t0) return 0;
    return Math.max(0, Math.floor((Date.now() - t0) / 1000));
    // Re-derive every tick — dep list intentionally covers the wake events.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerAnchor, callState, isActive, (Date.now() / 1000) | 0]);

  const phase = useMemo(
    () =>
      isActive
        ? resolveCallPhase({
            direction,
            callState,
            outgoingCallStatus,
            customerRingingActive,
            hasBridge,
            agentFirst: Boolean(activeCallMeta?.agent_first),
            customerConnected: Boolean(activeCallMeta?.customer_connected),
          })
        : { text: 'Ended', tone: 'mute' },
    [isActive, direction, callState, outgoingCallStatus, customerRingingActive, hasBridge,
      activeCallMeta?.agent_first, activeCallMeta?.customer_connected],
  );

  const dirIcon = direction === 'incoming'
    ? <FiPhoneIncoming size={11} />
    : direction === 'outgoing'
      ? <FiPhoneOutgoing size={11} />
      : null;
  const dirLabel = direction === 'incoming'
    ? 'Inbound'
    : direction === 'outgoing'
      ? 'Outbound'
      : '';

  // Street/City/State/Country are dedicated columns the Contacts
  // "Create Contact" form saves directly. Email has no dedicated column —
  // it's saved into the contact's additional_details JSONB blob (via the
  // same form) and read back with fuzzy key matching, above, since a
  // disposition-form-driven write can use any field name. Language used to
  // be shown here too but was dropped entirely — there's no real DB column
  // for it and it always defaulted to "English" regardless of the
  // contact's actual language, so it was a misleading always-present
  // falsy value rather than real data.
  const email = findAdditionalDetail(callerContact?.additional_details, 'email', 'emailaddress', 'emailid');
  const company = callerContact?.company || activeCallMeta?.company || null;
  const detail = [
    { icon: <FiTarget size={12} />,   label: 'Campaign', value: activeCallMeta?.campaign_name || '—' },
    { icon: <FiHome size={12} />,     label: 'Street',   value: callerContact?.street || '—' },
    { icon: <FiMapPin size={12} />,   label: 'City',     value: callerContact?.city || '—' },
    { icon: <FiFlag size={12} />,     label: 'State',    value: callerContact?.state || '—' },
    { icon: <FiGlobe size={12} />,    label: 'Country',  value: callerContact?.country || '—' },
    { icon: <FiBriefcase size={12} />, label: 'Company', value: company || '—' },
    { icon: <FiMail size={12} />,     label: 'Email',    value: email || '—' },
  ];
  // The grid keeps 6 cells. Campaign is always the first, even when empty; Email shows only when
  // it has a value. The other 5: filled ones first (contact columns, then its custom fields, each
  // in its own order), empty cells only to make up 6. Everything else sits behind the toggle.
  const extraDetails = contactExtraDetails(callerContact?.additional_details, callerContact?.custom_field_keys);
  const [campaignCell, ...contactCells] = detail
    .filter((d) => d.label !== 'Email' || d.value !== '—')
    .map((d) => ({ ...d, key: d.label }));
  const ranked = [
    ...contactCells,
    ...extraDetails.map((d) => ({ icon: <FiTag size={12} />, label: d.label, value: d.value, key: d.id })),
  ].sort((a, b) => Number(a.value === '—') - Number(b.value === '—'));
  const gridDetails = [campaignCell, ...ranked.slice(0, GRID_CELLS - 1)];
  const moreDetails = ranked.slice(GRID_CELLS - 1);
  const [showExtra, setShowExtra] = usePersistedState('cti/CustomerProfileCard:showExtra', false);

  if (!hasContact && !isActive) {
    return (
      <div className="cpc-card cpc-card--empty">
        <div className="cpc-head">
          <div className="cpc-avatar cpc-avatar--empty" aria-hidden>—</div>
          <div className="cpc-identity">
            <div className="cpc-name-row">
              <span className="cpc-name cpc-name--empty">No active caller</span>
            </div>
            <div className="cpc-numbers cpc-numbers--empty">
              Customer details will appear here when a call rings or when you pick a contact from the right rail.
            </div>
          </div>
        </div>

        {/* Same detail grid as the active card, all cells falling back to
            em-dash — keeps the layout stable instead of collapsing away
            while idle. */}
        <div className="cpc-grid">
          {gridDetails.map((d) => (
            <div className="cpc-cell" key={d.key}>
              <div className="cpc-cell-head">
                <span className="cpc-cell-ico">{d.icon}</span>
                <span className="cpc-cell-label">{d.label}</span>
              </div>
              <div className="cpc-cell-value" title={String(d.value)}>{d.value}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="cpc-card" data-active={isActive ? 'true' : 'false'}>
      {/* Header row — avatar · identity · timer */}
      <div className="cpc-head">
        <div className="cpc-avatar" aria-hidden>
          {initials(callerContact?.first_name, callerContact?.last_name, primary)}
        </div>
        <div className="cpc-identity">
          <div className="cpc-name-row">
            <span className="cpc-name">{fullName}</span>
          </div>
          <div className="cpc-numbers">
            {primary && <span className="cpc-num">{primary}</span>}
            {alt1 && <span className="cpc-num">{alt1}</span>}
            {alt2 && <span className="cpc-num">{alt2}</span>}
          </div>
        </div>
        <div className="cpc-timer-block">
          <div className="cpc-timer">{formatSeconds(durationSec)}</div>
          <div className="cpc-state">
            {dirIcon}
            {dirLabel && <span>{dirLabel}</span>}
            {dirLabel && phase.text && <span className="cpc-state-sep">·</span>}
            <span className={`call-evt call-evt--${callPhaseEvt(phase)}`}>{phase.text}</span>
          </div>
          {showQualityIndicator && (
            <div className="mt-1 flex justify-end">
              <CallQualityIndicator compact />
            </div>
          )}
        </div>
      </div>

      {/* Detail grid — key/value pairs from the contact record */}
      <div className="cpc-grid">
        {gridDetails.map((d) => (
          <div className="cpc-cell" key={d.key}>
            <div className="cpc-cell-head">
              <span className="cpc-cell-ico">{d.icon}</span>
              <span className="cpc-cell-label">{d.label}</span>
            </div>
            <div className="cpc-cell-value" title={String(d.value)}>{d.value}</div>
          </div>
        ))}
      </div>

      {moreDetails.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setShowExtra((v) => !v)}
            aria-expanded={showExtra}
            className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15"
          >
            <FiTag size={11} />
            More details ({moreDetails.length})
            <FiChevronDown size={12} className={showExtra ? 'rotate-180 transition-transform' : 'transition-transform'} />
          </button>
          {showExtra && (
            <div className="cpc-grid mt-2">
              {moreDetails.map((d) => (
                <div className="cpc-cell" key={d.key}>
                  <div className="cpc-cell-head">
                    <span className="cpc-cell-ico">{d.icon}</span>
                    <span className="cpc-cell-label">{d.label}</span>
                  </div>
                  <div className="cpc-cell-value" title={d.value}>{d.value}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
